// src/pages/PreviewConfirmation.jsx
import axios from "axios";
import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useStyle } from "../context/StyleContext";

const OVERLAY_SRC = "./Overlay2.jpg"; // replace with your actual overlay image path

const PreviewConfirmation = () => {
  const navigate = useNavigate();
  const styles = useStyle();

  // Prevent double-increment
  const incrementedRef = useRef(false);

  // State
  const [photos, setPhotos] = useState([]);
  const [isFiltreApplied, setIsFiltreApplied] = useState(false);
  const [retakeAllowed, setRetakeAllowed] = useState(true);
  const [theme, setTheme] = useState("light");

  // Load captured photos & retake status
  useEffect(() => {
    try {
      const stored = JSON.parse(localStorage.getItem("capturedPhotos"));
      const retakes = JSON.parse(localStorage.getItem("retakeStatus")) || {
        retakes: 0,
      };
      if (stored) {
        setPhotos(stored);
        if (retakes.retakes >= 1) setRetakeAllowed(false);
      } else {
        navigate("/photo-capture");
      }
    } catch (err) {
      console.error("Error reading storage:", err);
    }
  }, [navigate]);

  // Toggle filtre on/off
  const toggleFiltre = () => setIsFiltreApplied((p) => !p);

  // Draw image into canvas with current filtre + overlay, return dataURL
  const applyCanvasFilter = (src) =>
    new Promise((resolve, reject) => {
      const base = new Image();
      base.crossOrigin = "Anonymous";
      base.src = src;
      base.onload = () => {
        const canvas = document.createElement("canvas");
        canvas.width = base.width;
        canvas.height = base.height;
        const ctx = canvas.getContext("2d");

        // lighten the filtre: slightly lower contrast, higher brightness
        ctx.filter = isFiltreApplied
          ? "grayscale(100%) contrast(0.8) brightness(1.5) sepia(0.1)"
          : "none";
        ctx.drawImage(base, 0, 0);

        if (isFiltreApplied) {
          const overlay = new Image();
          overlay.crossOrigin = "Anonymous";
          overlay.src = OVERLAY_SRC;
          overlay.onload = () => {
            // draw overlay at 20% opacity, then multiply
            ctx.globalCompositeOperation = "overlay";
            ctx.globalAlpha = 0.2; // reduced opacity
            ctx.drawImage(overlay, 0, 0, canvas.width, canvas.height);
            // restore defaults
            ctx.globalAlpha = 1.0;
            ctx.globalCompositeOperation = "source-over";
            resolve(canvas.toDataURL("image/png"));
          };
          overlay.onerror = reject;
        } else {
          resolve(canvas.toDataURL("image/png"));
        }
      };
      base.onerror = reject;
    });

  // Confirm: convert & save filtered+textured images, increment once, then navigate
  const handleConfirm = async () => {
    if (incrementedRef.current) return;
    incrementedRef.current = true;

    try {
      const dataUrls = await Promise.all(
        photos.map((src) => applyCanvasFilter(src))
      );

      const saved = dataUrls.map((d) => ({
        src: d,
        filter: isFiltreApplied ? "filtre" : "none",
      }));
      localStorage.setItem("filteredPhotos", JSON.stringify(saved));
      localStorage.setItem("printTheme", theme);

      await axios.patch(
        `${process.env.REACT_APP_API_URL}/api/restaurants/increment/${styles._id}`
      );
    } catch (err) {
      console.error("Error applying filtre + overlay:", err);
      const fallback = photos.map((src) => ({
        src,
        filter: isFiltreApplied ? "filtre" : "none",
      }));
      localStorage.setItem("filteredPhotos", JSON.stringify(fallback));
      localStorage.setItem("printTheme", theme);
    } finally {
      navigate("/print-page");
    }
  };

  // Single retake allowed
  const handleRetake = () => {
    try {
      const status = JSON.parse(localStorage.getItem("retakeStatus")) || {
        retakes: 0,
      };
      if (status.retakes < 1) {
        status.retakes += 1;
        localStorage.setItem("retakeStatus", JSON.stringify(status));
        navigate("/photo-capture");
      } else {
        setRetakeAllowed(false);
      }
    } catch (err) {
      console.error("Error handling retake:", err);
    }
  };

  // Theme switch
  const handleThemeChange = (t) => setTheme(t);

  return (
    <div className="relative min-h-screen flex flex-col items-center justify-center overflow-hidden">
      <motion.h1
        className="text-7xl font-bold mb-8"
        initial={{ opacity: 0, y: -50 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 1 }}
        style={{ color: styles.textColor, fontFamily: styles.fontFamily }}
      >
        Preview &amp; Confirm
      </motion.h1>

      {/* Photo Previews */}
      <div className="flex flex-col items-center justify-center mb-8">
        <motion.div
          className="flex flex-col items-center space-y-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1 }}
        >
          {photos.map((src, i) => (
            <motion.div
              key={i}
              className="relative"
              whileHover={{ scale: 1.05 }}
              transition={{ duration: 0.3 }}
              style={{
                width: "60%",
                borderRadius: "10px",
                border: `8px solid ${styles.borderColor}`,
                overflow: "hidden",
              }}
            >
              <img
                src={src}
                alt={`Capture ${i + 1}`}
                className="block w-full h-auto"
                style={{
                  filter: isFiltreApplied
                    ? "grayscale(100%) contrast(1.2) brightness(0.9) sepia(0.2)"
                    : "none",
                }}
              />
              {isFiltreApplied && (
                <img
                  src={OVERLAY_SRC}
                  alt="Texture overlay"
                  className="pointer-events-none absolute inset-0 w-full h-full object-cover mix-blend-multiply"
                />
              )}
            </motion.div>
          ))}
        </motion.div>
      </div>

      {/* Theme Selectors */}
      <div className="mt-4 flex space-x-4">
        <motion.button
          onClick={() => handleThemeChange("light")}
          className="px-6 py-3 font-semibold rounded-lg shadow-lg hover:shadow-xl transform hover:scale-105 transition duration-300 ease-in-out relative inline-flex items-center justify-center px-10 py-4 overflow-hidden font-medium tracking-tighter rounded-lg group"
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.95 }}
          style={{
            backgroundColor: styles.backgroundColor,
            fontFamily: styles.fontFamily,
            border:
              theme === "light"
                ? `2px solid ${styles.borderColor}`
                : "2px solid transparent",
          }}
        >
          <span className="absolute w-0 h-0 transition-all duration-500 ease-out bg-green-500 rounded-full group-hover:w-56 group-hover:h-56" />
          <span className="absolute inset-0 w-full h-full -mt-1 rounded-lg opacity-30 bg-gradient-to-b from-transparent via-transparent to-gray-700" />
          <span
            className="relative text-3xl p-5"
            style={{
              fontFamily: styles.fontFamily,
              fontWeight: "bold",
              color: styles.textColor,
            }}
          >
            {styles.whiteButtonName}
          </span>
        </motion.button>

        <motion.button
          onClick={() => handleThemeChange("dark")}
          className="px-6 py-3 font-semibold rounded-lg shadow-lg hover:shadow-xl transform hover:scale-105 transition duration-300 ease-in-out relative inline-flex items-center justify-center px-10 py-4 overflow-hidden font-medium tracking-tighter rounded-lg group"
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.95 }}
          style={{
            backgroundColor: styles.backgroundColor,
            fontFamily: styles.fontFamily,
            border:
              theme === "dark"
                ? `2px solid ${styles.borderColor}`
                : "2px solid transparent",
          }}
        >
          <span className="absolute w-0 h-0 transition-all duration-500 ease-out bg-green-500 rounded-full group-hover:w-56 group-hover:h-56" />
          <span className="absolute inset-0 w-full h-full -mt-1 rounded-lg opacity-30 bg-gradient-to-b from-transparent via-transparent to-gray-700" />
          <span
            className="relative text-3xl p-5"
            style={{
              fontFamily: styles.fontFamily,
              fontWeight: "bold",
              color: styles.textColor,
            }}
          >
            {styles.blackStripButtonName}
          </span>
        </motion.button>
      </div>

      {/* Confirm & Retake */}
      <div className="mt-8 flex space-x-4">
        <motion.button
          onClick={handleConfirm}
          className="px-6 py-3 font-semibold rounded-lg shadow-lg hover:shadow-xl transform hover:scale-105 transition duration-300 ease-in-out relative inline-flex items-center justify-center px-10 py-4 overflow-hidden font-medium tracking-tighter rounded-lg group"
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.95 }}
          style={{
            backgroundColor: styles.backgroundColor,
            fontFamily: styles.fontFamily,
          }}
        >
          <span className="absolute w-0 h-0 transition-all duration-500 ease-out bg-green-500 rounded-full group-hover:w-56 group-hover:h-56" />
          <span className="absolute inset-0 w-full h-full -mt-1 rounded-lg opacity-30 bg-gradient-to-b from-transparent via-transparent to-gray-700" />
          <span
            className="relative text-5xl p-5"
            style={{
              fontFamily: styles.fontFamily,
              fontWeight: "bold",
              color: styles.textColor,
            }}
          >
            Confirm
          </span>
        </motion.button>

        <motion.button
          onClick={handleRetake}
          disabled={!retakeAllowed}
          className={`px-6 py-3 font-semibold rounded-lg shadow-lg hover:shadow-xl transform hover:scale-105 transition duration-300 ease-in-out relative inline-flex items-center justify-center px-10 py-4 overflow-hidden font-medium tracking-tighter rounded-lg group ${
            !retakeAllowed ? "opacity-50 cursor-not-allowed" : ""
          }`}
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.95 }}
          style={{
            backgroundColor: styles.backgroundColor,
            fontFamily: styles.fontFamily,
          }}
        >
          <span className="absolute w-0 h-0 transition-all duration-500 ease-out:bg-green-500 rounded-full group-hover:w-56 group-hover:h-56" />
          <span className="absolute inset-0 w-full h-full -mt-1 rounded-lg opacity-30:bg-gradient-to-b from-transparent via-transparent to-gray-700" />
          <span
            className="relative text-5xl p-5"
            style={{
              fontFamily: styles.fontFamily,
              fontWeight: "bold",
              color: styles.textColor,
            }}
          >
            Retake
          </span>
        </motion.button>
      </div>

      {/* Single “Filtre” Toggle */}
      {/* <div className="mt-6">
        <motion.button
          onClick={toggleFiltre}
          className="px-6 py-3 font-semibold rounded-lg shadow-lg hover:shadow-xl transform hover:scale-105 transition duration-300 ease-in-out relative inline-flex items-center justify-center px-10 py-4 overflow-hidden font-medium tracking-tighter rounded-lg group"
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.95 }}
          style={{
            backgroundColor: styles.backgroundColor,
            fontFamily: styles.fontFamily,
            border: isFiltreApplied
              ? `2px solid ${styles.borderColor}`
              : "2px solid transparent",
          }}
        >
          <span className="absolute w-0 h-0 transition-all duration-500 ease-out:bg-green-500 rounded-full group-hover:w-56 group-hover:h-56" />
          <span className="absolute inset-0 w-full h-full -mt-1 rounded-lg opacity-30:bg-gradient-to-b from-transparent via-transparent to-gray-700" />
          <span
            className="relative text-5xl p-5"
            style={{
              fontFamily: styles.fontFamily,
              fontWeight: "bold",
              color: styles.textColor,
            }}
          >
            Filter
          </span>
        </motion.button>
      </div> */}
    </div>
  );
};

export default PreviewConfirmation;
